/**
 * 画布级命令（M3-11 / DESIGN.md §6.1 裁剪）。
 *
 * 尺寸修改本身是纯结构命令；裁剪的像素平移由调用方以像素事务完成，
 * 经 store.dispatchWithPixels 与本命令合成**一条**历史。
 */
import type { Command } from '../command-bus';
import type { Guide, SavedPath } from '../../document/types';

const STRUCTURAL_COST = 128;

export function createSetCanvasSizeCommand(width: number, height: number): Command {
  return {
    kind: 'canvas.setSize',
    label: '裁剪',
    cost: STRUCTURAL_COST,
    record: { kind: 'canvas.setSize', args: { width, height } },
    apply(draft) {
      draft.setCanvasSize(width, height);
    },
    invert(document) {
      return createSetCanvasSizeCommand(document.size.width, document.size.height);
    },
  };
}

/**
 * 替换整份参考线列表——新建/删除/清空/切换锁定都走这一条命令，
 * 只是调用方传进来的新数组不同（跟 setCanvasSize 同一个"结构命令只管
 * 落地，语义留给调用方"的分工）。
 */
export function createSetGuidesCommand(guides: readonly Guide[]): Command {
  return {
    kind: 'canvas.setGuides',
    label: '参考线',
    cost: STRUCTURAL_COST,
    record: {
      kind: 'canvas.setGuides',
      args: { guides: guides.map((g) => ({ axis: g.axis, position: g.position, locked: g.locked })) },
    },
    apply(draft) {
      draft.setGuides(guides);
    },
    invert(document) {
      return createSetGuidesCommand(document.guides);
    },
  };
}

/**
 * 替换整份 Paths 面板列表（DESIGN.md §7.1 补充，ADR-0011）——新建/重命名/
 * 删除/复制都走这一条命令，跟 `createSetGuidesCommand` 同一个"结构命令
 * 只管落地，语义留给调用方"的分工，见 `app/paths.ts`。
 */
export function createSetPathsCommand(paths: readonly SavedPath[]): Command {
  return {
    kind: 'canvas.setPaths',
    label: '路径',
    cost: STRUCTURAL_COST,
    record: {
      kind: 'canvas.setPaths',
      args: {
        paths: paths.map((p) => ({
          id: p.id,
          name: p.name,
          path: { commands: p.path.commands, fillRule: p.path.fillRule },
        })),
      },
    },
    apply(draft) {
      draft.setPaths(paths);
    },
    invert(document) {
      return createSetPathsCommand(document.paths);
    },
  };
}
